import { ActionIcon, Box, Group, Text } from '@mantine/core';
import { LogOut } from 'react-feather';
import type { ParticipantActiveSessionLeader } from '../../../../services/api/participantActiveSession';
import { JoinVideoCall } from '../../../../components/session/JoinVideoCall';

/**
 * Top bar for the participant active session screen (MED-805 AC #3).
 *
 * Renders the dynamic leader pill, optional AI listening pill (when the
 * session's modality has AI features enabled per Decision B1 from MED-792),
 * and the exit button on the right. Sticky at the top of the immersive dark
 * layout.
 */

const LEADER_PILL_BG = 'rgba(255, 255, 255, 0.08)';
const AI_PILL_BG = 'rgba(233, 162, 59, 0.18)';
const AI_DOT_COLOR = '#E9A23B';
const AVATAR_BG = '#E07A3B';

function deriveInitials(first: string, displayName: string): string {
  const parts = (displayName ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (first?.[0] ?? '?').toUpperCase();
}

export interface ActiveSessionTopBarProps {
  leader: ParticipantActiveSessionLeader;
  aiFeaturesEnabled: boolean;
  aiListeningEnabled: boolean;
  /** MED-953 — live paused state (coach control); pill flips in real time. */
  aiPaused?: boolean;
  /** MED-1112 — resolved meeting link; the "Join video call" button renders only when present. */
  meetingLink?: string | null;
  onExit: () => void;
}

export function ActiveSessionTopBar({
  leader,
  aiFeaturesEnabled,
  aiListeningEnabled,
  aiPaused = false,
  meetingLink,
  onExit,
}: ActiveSessionTopBarProps) {
  const initials = deriveInitials(leader.first_name, leader.display_name);
  const leaderName =
    leader.display_name?.trim() || leader.first_name || 'Your leader';

  return (
    <Box
      component="header"
      data-testid="participant-active-top-bar"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 5,
        backgroundColor: '#0E1B33',
        padding: '12px 24px',
      }}
    >
      <Group justify="space-between" align="center" wrap="nowrap">
        <Group gap={12} align="center" wrap="nowrap">
          <Group
            gap={8}
            align="center"
            wrap="nowrap"
            data-testid="participant-active-leader-pill"
            style={{
              backgroundColor: LEADER_PILL_BG,
              padding: '6px 12px 6px 6px',
              borderRadius: 999,
            }}
          >
            <Box
              w={28}
              h={28}
              style={{
                borderRadius: '50%',
                backgroundColor: AVATAR_BG,
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.3px',
              }}
            >
              {initials}
            </Box>
            <Text size="sm" c="white" fw={500}>
              {leaderName}{' '}
              <Text
                component="span"
                c="rgba(255,255,255,0.7)"
                fw={400}
                size="sm"
              >
                is leading
              </Text>
            </Text>
          </Group>

          {aiFeaturesEnabled && aiListeningEnabled && (
            // MED-953 AC4 — the pill stays visible while paused (transparency:
            // the group sees "AI is NOT listening" rather than nothing) and
            // flips back in real time when the coach resumes.
            <Group
              gap={8}
              align="center"
              wrap="nowrap"
              data-testid="participant-active-ai-pill"
              style={{
                backgroundColor: AI_PILL_BG,
                padding: '6px 12px',
                borderRadius: 999,
              }}
            >
              <Box
                w={8}
                h={8}
                style={{
                  borderRadius: '50%',
                  backgroundColor: aiPaused
                    ? 'rgba(255,255,255,0.45)'
                    : AI_DOT_COLOR,
                }}
              />
              <Text
                size="sm"
                c={aiPaused ? 'rgba(255,255,255,0.7)' : AI_DOT_COLOR}
                fw={500}
              >
                {aiPaused ? 'AI paused — not listening' : 'AI listening'}
              </Text>
            </Group>
          )}
        </Group>

        <Group gap={12} align="center" wrap="nowrap">
          {/* MED-1112 — meeting link, to the left of Exit (renders nothing when
              none / in-person). */}
          <JoinVideoCall href={meetingLink} variant="button" size="sm" />
          <ActionIcon
            variant="subtle"
            size={40}
            radius="xl"
            aria-label="Leave session"
            data-testid="participant-active-exit-button"
            onClick={onExit}
            style={{ backgroundColor: LEADER_PILL_BG, color: '#FFFFFF' }}
          >
            <LogOut size={18} />
          </ActionIcon>
        </Group>
      </Group>
    </Box>
  );
}
